import { useState, type FormEvent } from 'react';
import { useAccessToken } from '../auth/use-access-token.js';
import { useMeContext } from '../api/me-provider.js';
import { useWaivers } from '../api/use-waivers.js';
import { fillWaiver } from '../api/fill-waiver.js';
import { finishChild } from '../api/finish-child.js';
import { ApiError } from '../api/client.js';
import type { Person } from '../api/use-people.js';
import { ConsentBox } from './consent-box.js';
import { WaitNote } from './wait-note.js';
import { BusyLabel } from './busy-label.js';

/**
 * On an athlete's page (stage C; canvas row 11, "Parent · Lia: name, birthday,
 * waiver", approved 16 October).
 *
 * An athlete a coach added has no date of birth: the parent puts the name
 * right, gives the birthday, signs the waiver and answers photo and video -
 * Add a child's form, filled in from what the coach typed.
 *
 * An athlete already finished whose waiver is not the one in force (a coach's
 * booking does not ask for it; "You're booked" sends the parent here): the
 * waiver, with booking-waivers.tsx's words - and Photo and video with it while
 * that has never been answered, so the two are one block at the top
 * (Matthew, 28 October: "the waivers are separated").
 *
 * HELD (ruled 31 October, narrowing the 17 October ruling below): nothing
 * explains a waiver never signed until the parent tries to leave. profile-gate
 * brings them back, and only then does the page say why, so a parent who simply
 * signs never sees it.
 */

const FIELD_MESSAGES: Record<string, string> = {
  required: 'This is needed.',
  too_long: 'That is too long.',
  invalid_date: 'That is not a date.',
  in_the_future: 'That date has not happened yet.',
};

export function FinishAthlete({
  person,
  held,
  onDone,
}: {
  person: Person;
  /** The parent tried to leave and profile-gate brought them back. */
  held: boolean;
  onDone: () => void;
}) {
  const getToken = useAccessToken();
  const { state: meState } = useMeContext();
  const { state: waiverState, reload: reloadWaivers } = useWaivers();
  const details = person.date_of_birth === null && !person.is_account_holder;
  const asksMedia = person.image_video_allowed === null;

  const [firstName, setFirstName] = useState(person.first_name);
  const [lastName, setLastName] = useState(person.last_name);
  const [dateOfBirth, setDateOfBirth] = useState(person.date_of_birth ?? '');
  const [participation, setParticipation] = useState(false);
  const [media, setMedia] = useState(false);
  const [saving, setSaving] = useState(false);
  const [fieldError, setFieldError] = useState<{ field: string; code: string } | null>(null);
  const [formError, setFormError] = useState<string | null>(null);

  const profile = meState.status === 'ready' ? meState.me.profile : undefined;
  const guardian = profile ? `${profile.first_name} ${profile.last_name}` : '';
  const athlete = details
    ? `${firstName.trim()} ${lastName.trim()}`.trim()
    : `${person.first_name} ${person.last_name}`;
  const all = waiverState.status === 'ready' ? waiverState.waivers : null;
  // The holder signs the adult texts for themselves (W1).
  const waivers = all
    ? person.is_account_holder
      ? { participation: all.adult_participation, media: all.adult_media }
      : { participation: all.participation, media: all.media }
    : null;
  const first = details ? firstName.trim() || person.first_name : person.first_name;

  async function submit(event: FormEvent) {
    event.preventDefault();
    if (saving || !waivers?.participation) return;
    setFieldError(null);
    setFormError(null);
    if (!participation) {
      setFieldError({ field: 'participation', code: 'not_agreed' });
      return;
    }

    setSaving(true);
    const words = (template: string) => fillWaiver(template, guardian, athlete);
    try {
      await finishChild(getToken, person.client_id, {
        firstName: details ? firstName : person.first_name,
        lastName: details ? lastName : person.last_name,
        dateOfBirth: details ? dateOfBirth : (person.date_of_birth ?? ''),
        consents: {
          participation: {
            waiverId: waivers.participation.waiver_id,
            text: words(waivers.participation.template),
          },
          media:
            asksMedia && media && waivers.media
              ? { waiverId: waivers.media.waiver_id, text: words(waivers.media.template) }
              : null,
        },
      });
      onDone();
    } catch (cause) {
      if (cause instanceof ApiError && cause.field) {
        setFieldError({ field: cause.field, code: cause.code });
      } else if (cause instanceof ApiError && cause.code === 'waiver_changed') {
        reloadWaivers();
        setParticipation(false);
        setMedia(false);
        setFormError('The wording has changed. Read it again and approve.');
      } else {
        setFormError('That did not save. Try again.');
      }
      setSaving(false);
    }
  }

  const errorFor = (field: string) =>
    fieldError?.field === field
      ? fieldError.code === 'not_agreed'
        ? 'Approve this waiver to continue.'
        : (FIELD_MESSAGES[fieldError.code] ?? 'That is not right.')
      : null;

  return (
    <form className="finish-athlete" onSubmit={submit} noValidate>
      {held ? (
        <p className="state-note" data-tone="warn" role="alert">
          {`Sign ${first}'s waiver to continue.`}
        </p>
      ) : null}
      {details ? (
        <div className="panel">
          <Field
            id="first-name"
            label="First name"
            value={firstName}
            onChange={setFirstName}
            error={errorFor('firstName')}
            autoComplete="section-athlete given-name"
          />
          <Field
            id="last-name"
            label="Last name"
            value={lastName}
            onChange={setLastName}
            error={errorFor('lastName')}
            autoComplete="section-athlete family-name"
          />
          <Field
            id="date-of-birth"
            label="Date of birth"
            type="date"
            value={dateOfBirth}
            onChange={setDateOfBirth}
            error={errorFor('dateOfBirth')}
          />
        </div>
      ) : null}

      <span className="section-label">Waiver for {first}</span>
      {/* Nothing to explain a waiver never signed (ruled 17 October); only why
          one signed already is asked for again. */}
      {person.waiver_signed_at ? (
        <p className="consent-why">
          {`Our waiver has been updated since you signed it for ${first}, please read and approve it again.`}
        </p>
      ) : null}
      {waiverState.status === 'loading' ? <WaitNote className="empty">Checking…</WaitNote> : null}
      {waivers?.participation ? (
        <ConsentBox
          id="participation"
          waiver={waivers.participation}
          guardian={guardian}
          athlete={athlete}
          checked={participation}
          onChange={(next) => {
            setParticipation(next);
            if (next && fieldError?.field === 'participation') setFieldError(null);
          }}
          error={errorFor('participation')}
          required
        />
      ) : null}
      {asksMedia && waivers?.media ? (
        <ConsentBox
          id="media"
          waiver={waivers.media}
          guardian={guardian}
          athlete={athlete}
          checked={media}
          onChange={setMedia}
        />
      ) : null}

      {formError ? <p className="state-note">{formError}</p> : null}

      <button className="primary" type="submit" disabled={saving || !waivers?.participation} aria-busy={saving}>
        <BusyLabel busy={saving} working="Saving…">
          Save
        </BusyLabel>
      </button>
    </form>
  );
}

function Field({
  id,
  label,
  value,
  onChange,
  error,
  type = 'text',
  autoComplete,
}: {
  id: string;
  label: string;
  value: string;
  onChange: (next: string) => void;
  error: string | null;
  type?: string;
  /** The athlete's name, suggested apart from the parent's own (ruled 22 October: suggestions on in the parent's forms). */
  autoComplete?: string;
}) {
  return (
    <label className="field" htmlFor={id}>
      <span>{label}</span>
      <input
        id={id}
        type={type}
        value={value}
        autoComplete={autoComplete}
        aria-invalid={error ? true : undefined}
        aria-describedby={error ? `${id}-error` : undefined}
        onChange={(event) => onChange(event.target.value)}
      />
      {error ? (
        <span className="field-error" id={`${id}-error`}>
          {error}
        </span>
      ) : null}
    </label>
  );
}
